import React, { useState, useEffect, useCallback, useRef } from "react";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const fmtTs = (ts) => {
  if (!ts) return "—";
  const n = Number(ts);
  if (!n) return "—";
  return new Date(n * 1000).toLocaleString();
};

export default function EtsyIntegrationPage({ store }) {
  const storeKey = store === "designshop" ? "designshop" : "vibratovault";
  const storeLabel = storeKey === "designshop" ? "Design Shop" : "Vibrato Vault";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("orders");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [settings, setSettings] = useState({
    connected: false, appConfigured: false, shopId: "", shopName: "",
    lastSynced: "", tokenExpiresAt: "", scopes: "",
  });
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  // CEO-only app credential form
  const [cred, setCred] = useState({ empId: "", password: "", clientId: "", clientSecret: "" });
  const [credSaving, setCredSaving] = useState(false);
  const [showCredForm, setShowCredForm] = useState(false);
  const connectPoll = useRef(null);

  const fetchSettings = useCallback(() => {
    fetch(`${API_URL}/api/etsy/settings?store=${storeKey}`)
      .then((r) => r.json())
      .then((s) => setSettings((prev) => ({ ...prev, ...s })))
      .catch(() => {});
  }, [storeKey]);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/etsy/orders?store=${storeKey}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else if (data.orders) setOrders(data.orders);
        setLoading(false);
      })
      .catch((err) => { setError(`Fetch failed: ${err.message}`); setLoading(false); });
  }, [storeKey]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { if (tab === "orders") fetchOrders(); }, [tab, fetchOrders]);

  // Auto-dismiss the success toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Surface the OAuth callback result if the user landed back here with a param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("etsy_connected")) {
      setToast("Connected to Etsy ✓");
      fetchSettings();
    } else if (params.get("etsy_error")) {
      setError(`Etsy connection failed: ${params.get("etsy_error")}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallback auto-sync while the page is open (server poller also runs every 5 min)
  useEffect(() => {
    if (!settings.connected) return undefined;
    const iv = setInterval(() => {
      fetch(`${API_URL}/api/etsy/sync?store=${storeKey}`, { method: "POST" })
        .then((r) => r.json())
        .then((d) => { if (d && (d.synced > 0)) { fetchOrders(); fetchSettings(); } })
        .catch(() => {});
    }, 60000);
    return () => clearInterval(iv);
  }, [settings.connected, storeKey, fetchOrders, fetchSettings]);

  // Clean up the connect poller on unmount
  useEffect(() => () => { if (connectPoll.current) clearInterval(connectPoll.current); }, []);

  const connect = async () => {
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/etsy/oauth/start?store=${storeKey}`);
      const data = await r.json();
      if (data.error) { setError(data.error); return; }
      window.open(data.authorizeUrl, "_blank", "noopener");
      // Poll for the connection to complete (callback runs in the new tab)
      setConnecting(true);
      if (connectPoll.current) clearInterval(connectPoll.current);
      let tries = 0;
      connectPoll.current = setInterval(async () => {
        tries += 1;
        try {
          const s = await fetch(`${API_URL}/api/etsy/settings?store=${storeKey}`).then((x) => x.json());
          if (s.connected) {
            setSettings((prev) => ({ ...prev, ...s }));
            setConnecting(false);
            setToast("Connected to Etsy ✓");
            clearInterval(connectPoll.current);
          } else if (tries > 75) { // ~2.5 min
            setConnecting(false);
            clearInterval(connectPoll.current);
          }
        } catch (e) { /* ignore transient errors */ }
      }, 2000);
    } catch (e) { setError(`Could not start Etsy connect: ${e.message}`); }
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${storeLabel} from Etsy? Inventory will stop auto-syncing until reconnected.`)) return;
    await fetch(`${API_URL}/api/etsy/disconnect?store=${storeKey}`, { method: "POST" }).catch(() => {});
    fetchSettings();
    setToast("Disconnected from Etsy");
  };

  const syncNow = async () => {
    setSyncing(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/etsy/sync?store=${storeKey}`, { method: "POST" });
      const d = await r.json();
      if (d.error) setError(d.error);
      else {
        const errs = (d.errors || []).length;
        setToast(`Synced ${d.synced} • skipped ${d.skipped}${errs ? ` • ${errs} error${errs > 1 ? "s" : ""}` : ""}`);
        fetchOrders();
        fetchSettings();
      }
    } catch (e) { setError(`Sync failed: ${e.message}`); }
    setSyncing(false);
  };

  const saveCredentials = async () => {
    setCredSaving(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/etsy/app-credentials`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cred),
      });
      const d = await r.json();
      if (d.error) setError(d.error);
      else {
        setToast("Etsy app credentials saved ✓");
        setShowCredForm(false);
        setCred({ empId: "", password: "", clientId: "", clientSecret: "" });
        fetchSettings();
      }
    } catch (e) { setError(`Save failed: ${e.message}`); }
    setCredSaving(false);
  };

  const filteredOrders = orders.filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (o.title || "").toLowerCase().includes(s)
      || (o.sku || "").toLowerCase().includes(s)
      || (o.buyerName || "").toLowerCase().includes(s)
      || String(o.receiptId).includes(s);
  });

  const tabs = [
    { key: "orders", label: "Orders" },
    { key: "connect", label: "Connection" },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1 }}>Etsy Integration</h2>
          <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginTop: 2 }}>direct Etsy API · {storeLabel}</div>
        </div>
        {settings.connected && (
          <button onClick={syncNow} disabled={syncing} style={{ ...S.addBtn, opacity: syncing ? 0.6 : 1, cursor: syncing ? "wait" : "pointer" }}>
            {syncing ? "Syncing…" : "Sync Now"}
          </button>
        )}
      </div>

      <div style={{ ...S.nav, marginBottom: 20 }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setSearch(""); }} style={{ ...S.navBtn, ...(tab === t.key ? S.navBtnActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      {toast && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(52,211,153,0.12)", border: "1px solid #34d399", borderRadius: 0, fontSize: 14, color: "#0c3a2a", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{toast}</span>
          <button onClick={() => setToast("")} style={{ background: "none", border: "none", color: "#0c3a2a", cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", borderRadius: 0, fontSize: 14, color: "#ef4444", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 }}>x</button>
        </div>
      )}

      {/* Connection Tab */}
      {tab === "connect" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Connection status */}
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>Shop Connection</div>
            {settings.connected ? (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 0, background: "#34d399", display: "inline-block" }} />
                  <span style={{ fontSize: 16, color: "#0A0A0F", fontWeight: 600 }}>
                    Connected{settings.shopName ? ` — ${settings.shopName}` : ""}
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px", fontSize: 13, color: "rgba(10,10,15,0.58)", marginBottom: 16 }}>
                  {settings.shopId && (<><span>Shop ID</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{settings.shopId}</span></>)}
                  <span>Last synced</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{fmtTs(settings.lastSynced)}</span>
                  <span>Token valid until</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{fmtTs(settings.tokenExpiresAt)}</span>
                  {settings.scopes && (<><span>Scopes</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>{settings.scopes}</span></>)}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={syncNow} disabled={syncing} style={{ ...S.addBtn, opacity: syncing ? 0.6 : 1 }}>{syncing ? "Syncing…" : "Sync Now"}</button>
                  <button onClick={disconnect} style={{ ...S.clearBtn, fontSize: 13 }}>Disconnect</button>
                </div>
              </div>
            ) : (
              <div>
                <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 16, lineHeight: 1.5 }}>
                  {settings.appConfigured
                    ? `Authorize ${storeLabel} on Etsy. Orders will then sync directly — no Make.com. Only orders placed after you connect are deducted from inventory.`
                    : "Add the Etsy app credentials below first (CEO only), then connect the shop."}
                </p>
                <button onClick={connect} disabled={!settings.appConfigured || connecting} style={{ ...S.addBtn, opacity: (!settings.appConfigured || connecting) ? 0.5 : 1, cursor: (!settings.appConfigured || connecting) ? "not-allowed" : "pointer" }}>
                  {connecting ? "Waiting for Etsy…" : `Connect ${storeLabel} to Etsy`}
                </button>
                {connecting && (
                  <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginTop: 8 }}>Approve access in the Etsy tab that opened, then come back here.</div>
                )}
              </div>
            )}
          </div>

          {/* App credentials (CEO only) */}
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)" }}>Etsy App Credentials</div>
              {settings.appConfigured && !showCredForm && (
                <span style={{ fontSize: 13, color: "#34d399", display: "flex", alignItems: "center", gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                  Configured
                </span>
              )}
            </div>
            <p style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginBottom: 16, lineHeight: 1.5 }}>
              The keystring (client ID) + shared secret from your Etsy app. Stored server-side, never shown again. CEO credentials required to save.
            </p>
            {(!settings.appConfigured || showCredForm) ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 460 }}>
                <input value={cred.clientId} onChange={(e) => setCred({ ...cred, clientId: e.target.value })} placeholder="Keystring (Client ID)" style={S.input} />
                <input value={cred.clientSecret} onChange={(e) => setCred({ ...cred, clientSecret: e.target.value })} placeholder="Shared Secret (Client Secret)" type="password" style={S.input} />
                <div style={{ display: "flex", gap: 10 }}>
                  <input value={cred.empId} onChange={(e) => setCred({ ...cred, empId: e.target.value })} placeholder="CEO Employee ID" style={{ ...S.input, flex: 1 }} />
                  <input value={cred.password} onChange={(e) => setCred({ ...cred, password: e.target.value })} placeholder="CEO Password" type="password" style={{ ...S.input, flex: 1 }} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={saveCredentials} disabled={credSaving} style={{ ...S.addBtn, opacity: credSaving ? 0.6 : 1 }}>{credSaving ? "Saving…" : "Save Credentials"}</button>
                  {settings.appConfigured && <button onClick={() => setShowCredForm(false)} style={{ ...S.clearBtn, fontSize: 13 }}>Cancel</button>}
                </div>
              </div>
            ) : (
              <button onClick={() => setShowCredForm(true)} style={{ ...S.clearBtn, fontSize: 13 }}>Update Credentials</button>
            )}
          </div>

          {/* Callback URL reminder */}
          <div style={{ ...S.lineCard, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>Etsy App Setup</div>
            <p style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", lineHeight: 1.6 }}>
              In your Etsy app settings, add this exact <strong>Callback URL</strong> and grant the <code style={{ fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F" }}>transactions_r</code> scope:
            </p>
            <div style={{ marginTop: 8, padding: "10px 14px", background: "#FFFFFF", border: "2px solid #0A0A0F", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "#0033FF", wordBreak: "break-all" }}>
              {API_URL.replace(/\/$/, "")}/api/etsy/oauth/callback
            </div>
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {tab === "orders" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders..." style={{ ...S.input, flex: 1, maxWidth: 300 }} />
            <div style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{filteredOrders.length} orders</div>
            <button onClick={fetchOrders} disabled={loading} style={{ ...S.iconBtn, opacity: loading ? 0.5 : 1 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>
            </button>
          </div>

          {!settings.connected && orders.length === 0 && (
            <div style={{ ...S.lineCard, padding: 24, textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 16, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>Not connected to Etsy yet</div>
              <button onClick={() => setTab("connect")} style={S.addBtn}>Connect to Etsy</button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "rgba(10,10,15,0.58)" }}>Loading orders...</div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Receipt #</th>
                    <th style={S.th}>Buyer</th>
                    <th style={S.th}>Item</th>
                    <th style={S.th}>SKU</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Amount</th>
                    <th style={{ ...S.th, textAlign: "center" }}>Qty Deducted</th>
                    <th style={S.th}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => {
                    const items = o.items || [];
                    const hasMultiple = items.length > 1;
                    const isExpanded = expandedOrder === o.id;
                    return (
                      <React.Fragment key={o.id}>
                        <tr
                          onClick={() => hasMultiple && setExpandedOrder(isExpanded ? null : o.id)}
                          style={{ cursor: hasMultiple ? "pointer" : "default" }}
                        >
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>
                            {hasMultiple && (
                              <span style={{ display: "inline-block", width: 16, color: "rgba(10,10,15,0.58)", fontSize: 12 }}>{isExpanded ? "▾" : "▸"}</span>
                            )}
                            {o.receiptId}
                          </td>
                          <td style={{ ...S.td, fontSize: 14, color: "#0A0A0F" }}>{o.buyerName || "—"}</td>
                          <td style={{ ...S.td, maxWidth: 220 }}>
                            <div style={{ fontSize: 13, color: "#0A0A0F", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.title || "—"}</div>
                          </td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: o.sku ? "#0A0A0F" : "rgba(10,10,15,0.58)" }}>{o.sku || "—"}</td>
                          <td style={{ ...S.td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "#0033FF" }}>
                            {o.amount ? `$${Number(o.amount).toFixed(2)}` : "—"}
                          </td>
                          <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", color: o.quantityDeducted > 0 ? "#34d399" : "rgba(10,10,15,0.58)" }}>
                            {o.quantityDeducted > 0 ? `-${o.quantityDeducted}` : "0"}
                          </td>
                          <td style={{ ...S.td, fontSize: 13, color: "rgba(10,10,15,0.58)" }}>
                            {o.syncedAt ? new Date(o.syncedAt).toLocaleString() : "—"}
                          </td>
                        </tr>
                        {isExpanded && items.map((item, i) => (
                          <tr key={`${o.id}-${i}`} style={{ background: "#FFFFFF" }}>
                            <td style={{ ...S.td, paddingLeft: 32 }} />
                            <td style={S.td} />
                            <td style={{ ...S.td, maxWidth: 220 }}>
                              <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.title || "—"}</div>
                            </td>
                            <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "rgba(10,10,15,0.58)" }}>{item.sku || "—"}</td>
                            <td style={S.td} />
                            <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: item.deducted > 0 ? "#34d399" : "rgba(10,10,15,0.58)" }}>
                              {item.quantity || 1} qty{item.deducted > 0 ? ` (−${item.deducted})` : ""}
                            </td>
                            <td style={S.td} />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {filteredOrders.length === 0 && !loading && (
                    <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 24 }}>
                      {orders.length === 0 ? "No orders yet. Connect to Etsy and orders will sync here automatically." : "No matching orders"}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
